import { useEffect, useState } from 'react'
import { Ban, Loader2, RotateCcw } from 'lucide-react'
import { SectionCard } from '@/components/ui/SectionCard'
import { cn } from '@/lib/utils/helpers'
import { showApiError, showSuccess, showWarning } from '@/lib/utils/notifications'
import companyFormStyles from '@/features/company/styles/companyAdd.module.css'
import {
  fetchPmsResidentMatchStatus,
  updatePmsResidentMatchStatus,
} from '../../services/yardiResidentService'
import type { PmsResidentMatchStatus } from '../../types/yardiResidentSync'

const UNMATCHABLE_REASONS = [
  { id: 'HISTORICAL', label: 'Historical' },
  { id: 'NO_PMS_EQUIVALENT', label: 'No PMS equivalent' },
  { id: 'INSUFFICIENT_DATA', label: 'Insufficient data' },
  { id: 'INVALID_PMS_RECORD', label: 'Invalid PMS record' },
  { id: 'DUPLICATE_PMS_RECORD', label: 'Duplicate PMS record' },
  { id: 'BUSINESS_DECISION', label: 'Business decision not to map' },
]

export function ResidentMatchStatusCard({
  propertyId,
  companyId,
  propertyCode,
  residentDefaultId,
  contextLoading,
}: {
  propertyId: string | number
  companyId: string | number
  propertyCode: string
  residentDefaultId: string | number
  contextLoading: boolean
}) {
  const [status, setStatus] = useState<PmsResidentMatchStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [reason, setReason] = useState('HISTORICAL')
  const [note, setNote] = useState('')

  const loadStatus = async () => {
    if (!companyId || !propertyCode) return
    setLoading(true)
    try {
      const result = await fetchPmsResidentMatchStatus({
        property_id: propertyId,
        company_id: companyId,
        property_code: propertyCode,
        resident_default_id: residentDefaultId,
      })
      setStatus(result)
      if (result.unmatchable_reason) setReason(result.unmatchable_reason)
      if (result.admin_note) setNote(result.admin_note)
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (contextLoading) return
    void loadStatus()
  }, [companyId, propertyCode, propertyId, residentDefaultId, contextLoading])

  const saveStatus = async (nextStatus: 'UNMATCHABLE' | 'PENDING') => {
    if (nextStatus === 'UNMATCHABLE' && !reason) {
      showWarning('Select a reason before marking this resident unmatchable.')
      return
    }
    setSaving(true)
    try {
      const result = await updatePmsResidentMatchStatus({
        property_id: propertyId,
        company_id: companyId,
        property_code: propertyCode,
        resident_default_id: residentDefaultId,
        status: nextStatus,
        reason: nextStatus === 'UNMATCHABLE' ? reason : undefined,
        note: note.trim() || undefined,
      })
      setStatus(result)
      showSuccess(
        nextStatus === 'PENDING'
          ? 'Resident reset to pending for the next matching run.'
          : 'Resident marked unmatchable and will be skipped by automation.',
      )
    } catch (error) {
      showApiError(error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <SectionCard icon={Ban} title="Automation Status">
      {loading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading matching status…
        </div>
      ) : (
        <div className="space-y-4" data-testid="resident-match-status-card">
          <p className="text-sm text-slate-600">
            Current status:{' '}
            <span className="font-semibold text-slate-900">{status?.status || 'PENDING'}</span>
            {status?.shadow_decision ? (
              <span className="ml-2 text-xs text-slate-500">Shadow: {status.shadow_decision}</span>
            ) : null}
          </p>
          {status?.match_reason ? <p className="text-sm text-slate-500">{status.match_reason}</p> : null}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="block w-full max-w-[280px] text-sm font-medium text-slate-700">
              Unmatchable reason
              <select
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                data-testid="resident-match-unmatchable-reason"
              >
                {UNMATCHABLE_REASONS.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block w-full text-sm font-medium text-slate-700">
              Note
              <input
                value={note}
                onChange={(event) => setNote(event.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                placeholder="Optional administrator note"
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={saving || contextLoading}
              onClick={() => void saveStatus('UNMATCHABLE')}
              className={cn(companyFormStyles.btnPrimary, 'h-10 gap-2')}
              data-testid="resident-match-mark-unmatchable"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
              Mark Unmatchable
            </button>
            <button
              type="button"
              disabled={saving || contextLoading}
              onClick={() => void saveStatus('PENDING')}
              className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-300 px-4 text-sm font-medium text-slate-700"
              data-testid="resident-match-reset-pending"
            >
              <RotateCcw className="h-4 w-4" />
              Reset to Pending
            </button>
          </div>
        </div>
      )}
    </SectionCard>
  )
}
